import { type SportData } from "@/schemas";
import { SportType } from "@/contexts/composer-context";
import { ordinalSuffix } from "@/utils/numbers";

import type { SportTeamDetails } from "@/schemas/sport-team";

interface TeamRecord {
  wins: number;
  losses: number;
  ties?: number | null;
}

const formatRecord = (record: TeamRecord | undefined): string => {
  if (!record) return "";
  const { wins, losses, ties } = record;
  return ties && ties > 0 ? `${wins}-${losses}-${ties}` : `${wins}-${losses}`;
};

/**
 * NBA/CBB: "Eastern · 2nd" (conference + conference_rank)
 * MLB:     "NL East · 2nd" (division + division_rank)
 * Soccer:  "Premier League · 3rd" (league + league_position)
 */
const formatStanding = (
  team: SportTeamDetails | undefined,
): { label: string; rank: string } | null => {
  if (!team) return null;

  if (team.sport_type === SportType.NBA || team.sport_type === SportType.CBB) {
    if (!team.conference || team.conference_rank == null) return null;
    return {
      label: team.conference,
      rank: ordinalSuffix(team.conference_rank),
    };
  }

  if (team.sport_type === SportType.MLB) {
    if (!team.division || team.division_rank == null) return null;
    return {
      label: team.division,
      rank: ordinalSuffix(team.division_rank),
    };
  }

  if (team.sport_type === SportType.SOCCER) {
    if (!team.league || team.league_position == null) return null;
    return {
      label: team.league,
      rank: ordinalSuffix(team.league_position),
    };
  }

  return null;
};

function formatLiveStatusLabel(event: SportData) {
  const { clock_display, period, time } = event;
  if (clock_display) return clock_display;

  if (time || period) {
    return [time, period].filter(Boolean).join(" • ");
  }

  return null;
}

export { formatRecord, formatStanding, formatLiveStatusLabel };
